import { useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAccountBlock } from "@/hooks/useAccountBlock";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

// Paden die een geblokkeerde zorgverlener nog mag openen.
const ALLOWED = [/^\/mijn-praktijk\/abonnement/, /^\/mijn-praktijk\/instellingen\/opzeggen/, /^\/betaal\//, /^\/betaling-/, /^\/contact/, /^\/login/, /^\/reset-password/];
// Paden die achter de blokkade vallen.
const GUARDED = [/^\/mijn-praktijk/, /^\/berichten/, /^\/vacatures\/.+\/reageer/, /^\/aanbevelingen/, /^\/mijn-profiel/, /^\/facturatie/, /^\/instellingen/, /^\/nodig-uit/, /^\/affiliate/, /^\/samenwerkingen/, /^\/dashboard/];

const eur = (c: number) => `€ ${(c / 100).toFixed(2).replace(".", ",")}`;
const d = (s: number | null) => (s ? new Date(s * 1000).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" }) : "");

export const BlockedAccountGate = () => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data, refetch } = useAccountBlock();
  const [checking, setChecking] = useState(false);
  if (!data?.blocked) return null;

  const check = async () => {
    setChecking(true);
    const r = await refetch();
    setChecking(false);
    if (!r.data?.blocked) {
      toast.success("Alles is betaald. Je account staat weer aan.");
      qc.invalidateQueries({ queryKey: ["account-block", user?.id] });
    } else if (r.data.processing) toast.info("Je betaling wordt nog verwerkt. We controleren het vanzelf opnieuw.");
    else toast.info("We zien nog openstaande facturen.");
  };

  const covered = data.covered_count ?? 0;
  const paid = data.paid_count ?? 0;
  const status = (
    <>
      {covered > 1 && paid > 0 && <p className="text-sm font-medium">Al betaald: {paid} van {covered}</p>}
      {data.processing && <p className="text-sm text-muted-foreground">Een betaling wordt verwerkt. Dat kan een paar dagen duren.</p>}
    </>
  );
  const reason = data.block_type === "chargeback"
    ? "Een betaling aan ons is via je bank teruggeboekt. Daarom is je account tijdelijk geblokkeerd."
    : "Er staan facturen open die niet betaald zijn. Daarom is je account tijdelijk geblokkeerd.";

  const allowed = ALLOWED.some((r) => r.test(pathname));
  if (allowed) {
    if (!/^\/mijn-praktijk\/abonnement/.test(pathname)) return null;
    return (
      <div className="fixed top-0 inset-x-0 z-[60] bg-destructive text-destructive-foreground px-4 py-2 text-sm flex flex-wrap items-center justify-center gap-3">
        <AlertTriangle className="w-4 h-4" />
        <span>Je account is geblokkeerd. Openstaand: {eur(data.total_cents ?? 0)}{paid > 0 && covered > 1 ? ` (al betaald: ${paid} van ${covered})` : ""}.</span>
        {data.pay_link && <a href={data.pay_link} className="underline font-semibold">Alles betalen</a>}
        <button onClick={check} className="underline" disabled={checking}>{checking ? "Controleren…" : "Ik heb betaald, controleer nu"}</button>
      </div>
    );
  }
  if (!GUARDED.some((r) => r.test(pathname))) return null;
  if (pathname !== "/mijn-praktijk") return <Navigate to="/mijn-praktijk" replace />;

  return (
    <div className="fixed inset-0 z-[100] bg-foreground/70 flex items-center justify-center p-4" role="alertdialog" aria-modal="true" aria-labelledby="blocked-title">
      <div className="bg-background text-foreground rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 shadow-xl">
        <div className="flex items-center gap-2 text-destructive mb-2">
          <AlertTriangle className="w-5 h-5" />
          <h2 id="blocked-title" className="text-xl font-semibold">Je account is geblokkeerd</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-3">{reason} Betaal de openstaande facturen om het weer aan te zetten.</p>
        <div className="mb-3 space-y-1">{status}</div>
        {(data.invoices?.length ?? 0) > 0 && (
          <table className="w-full text-sm mb-4">
            <thead><tr className="text-muted-foreground text-left"><th className="py-1">Factuur</th><th>Periode</th><th className="text-right">Bedrag</th></tr></thead>
            <tbody>
              {data.invoices!.map((i) => (
                <tr key={i.id} className="border-t border-border">
                  <td className="py-1.5">{i.number ?? i.id}{i.processing && <span className="block text-xs text-muted-foreground">wordt verwerkt</span>}</td>
                  <td>{d(i.period_start)}{i.period_end ? ` – ${d(i.period_end)}` : ""}</td>
                  <td className="text-right">{eur(i.amount_cents)}</td>
                </tr>
              ))}
              <tr className="border-t border-border font-semibold"><td className="py-2" colSpan={2}>Totaal</td><td className="text-right">{eur(data.total_cents ?? 0)}</td></tr>
            </tbody>
          </table>
        )}
        <div className="flex flex-col gap-2">
          {data.pay_link && <Button asChild><a href={data.pay_link}>Alle openstaande facturen betalen</a></Button>}
          <Button variant="secondary" onClick={check} disabled={checking}>
            {checking && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Ik heb betaald, controleer nu
          </Button>
          <Button asChild variant="outline"><Link to="/mijn-praktijk/abonnement?tab=betaalmethode">Betaalmethode toevoegen</Link></Button>
          <Link to="/contact" className="text-sm text-center underline text-muted-foreground mt-1">Denk je dat dit een vergissing is? Neem contact op</Link>
        </div>
      </div>
    </div>
  );
};

export default BlockedAccountGate;
